import { inspect } from 'node-inspect-extracted';

type LogEntry = {
  type: 'log' | 'error' | 'warn' | 'info';
  args: unknown[]; // because browserConsole messages can be of different types (string, objects, etc.)
};

type PersistedLog = {
  log: string;
  createdAt: number;
  seq: number;
  sessionId: string;
};

const DB_NAME = 'browser-logs';
const DB_VERSION = 2;
const STORE_NAME = 'logs';
const CREATED_AT_INDEX: keyof PersistedLog = 'createdAt';

function randomSlug(length: number): string {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const randomValues = new Uint8Array(length);
  crypto.getRandomValues(randomValues);
  return Array.from(randomValues, (v) => chars[v % chars.length]).join('');
}

/** Session ID: ISO timestamp + 4-char slug for uniqueness across tabs */
const sessionId = `${new Date().toISOString()}-${randomSlug(4)}`;
let seq = 0;

/** Cached connection – cleared when closed (abnormally or explicitly) */
let dbRef: IDBDatabase | null = null;

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'id', autoIncrement: true });
        store.createIndex(CREATED_AT_INDEX, 'createdAt', { unique: false });
      }
    };
  });
}

function isConnectionClosedError(err: unknown): boolean {
  return err instanceof DOMException && (err.name === 'InvalidStateError' || err.name === 'DatabaseClosedError');
}

/** Returns a valid connection, reopening if closed. Clears cache on close event. */
async function getOrOpenDb(): Promise<IDBDatabase> {
  if (dbRef !== null) {
    const db = dbRef as IDBDatabase & { closed?: boolean };
    if ('closed' in db && db.closed) {
      dbRef = null;
    } else {
      return dbRef;
    }
  }

  const db = await openDb();
  dbRef = db;
  db.addEventListener('close', () => {
    dbRef = null;
  });
  return db;
}

async function deleteExpiredLogs(
  getConfig: () => { retentionWindow: number; retentionInterval: number },
): Promise<void> {
  const { retentionWindow, retentionInterval } = getConfig();
  const cutoff = Date.now() - retentionWindow;
  const db = await getOrOpenDb();

  try {
    await new Promise<void>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const index = store.index(CREATED_AT_INDEX);
      const range = IDBKeyRange.upperBound(cutoff, true);

      const request = index.openCursor(range);

      request.onsuccess = () => {
        const cursor = request.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      request.onerror = () => reject(request.error);

      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } finally {
    setTimeout(() => void deleteExpiredLogs(getConfig), retentionInterval);
  }
}

export function activateLogRetention(getConfig: () => { retentionWindow: number; retentionInterval: number }): void {
  void deleteExpiredLogs(getConfig);
}

function sanitize(value: unknown, seen = new WeakMap()): unknown {
  if (typeof value === 'function') {
    return '[Function]';
  }

  if (typeof value !== 'object' || value === null) {
    return value;
  }

  if (seen.has(value)) {
    return seen.get(value);
  }

  if (Array.isArray(value)) {
    const newArray: unknown[] = [];
    seen.set(value, newArray);
    value.forEach((v) => newArray.push(sanitize(v, seen)));
    return newArray;
  }

  if (value instanceof Error) {
    const message = value.stack || value.toString();
    seen.set(value, message);
    return message;
  }

  const out: Record<string, unknown> = {};
  seen.set(value, out);
  for (const [k, v] of Object.entries(value)) {
    out[k] = sanitize(v, seen);
  }
  return out;
}

function addLog(logEntry: LogEntry) {
  const line = inspect(sanitize({ sessionId, ...logEntry }), {
    compact: true,
    breakLength: Number.POSITIVE_INFINITY,
    depth: null,
  });
  const entry: PersistedLog = {
    log: line,
    createdAt: Date.now(),
    seq: seq++,
    sessionId,
  };

  void (async () => {
    try {
      const db = await getOrOpenDb();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, 'readwrite');
        const store = tx.objectStore(STORE_NAME);
        store.add(entry);
        tx.oncomplete = () => resolve();
        tx.onerror = () => reject(tx.error);
      });
    } catch (err) {
      if (isConnectionClosedError(err)) {
        dbRef = null;
        try {
          const db = await getOrOpenDb();
          await new Promise<void>((resolve, reject) => {
            const tx = db.transaction(STORE_NAME, 'readwrite');
            const store = tx.objectStore(STORE_NAME);
            store.add(entry);
            tx.oncomplete = () => resolve();
            tx.onerror = () => reject(tx.error);
          });
        } catch {
          // Silently ignore - logging must not break the app
        }
      }
      // Silently ignore other errors (quota, etc.) - logging must not break the app
    }
  })();
}

export async function downloadLogs(): Promise<void> {
  const db = await getOrOpenDb();

  const logs = await new Promise<PersistedLog[]>((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();

    request.onsuccess = () => {
      const rows = request.result as (PersistedLog & { id: number })[];
      resolve(rows.map(({ log, createdAt, seq, sessionId }) => ({ log, createdAt, seq, sessionId })));
    };
    request.onerror = () => reject(request.error);
  });

  const sorted = logs.sort((a, b) => {
    if (a.createdAt !== b.createdAt) return a.createdAt - b.createdAt;
    const sessionCompare = a.sessionId.localeCompare(b.sessionId);
    if (sessionCompare !== 0) return sessionCompare;
    return a.seq - b.seq;
  });

  const blob = new Blob(
    sorted.map(({ log }) => `${log} \n`),
    { type: 'application/x-ndjson' },
  );
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = 'browserConsole-logs.ndjson';
  link.click();

  URL.revokeObjectURL(url);
}

// init wrapper
function initBrowserLogs() {
  const c = console;
  c.info('Initializing browser logs collection wrapper...');

  void getOrOpenDb(); // Eagerly open connection for faster first log

  (['log', 'error', 'warn', 'info'] as const).forEach((method) => {
    const original = c[method].bind(c);
    c[method] = (...args: unknown[]) => {
      addLog({
        type: method,
        args,
      });
      original(...args);
    };
  });

  c.info('Browser logs collection wrapper initialized.');
}

if (import.meta.env?.VITE_APP_MODE !== 'cloud') {
  initBrowserLogs();
}
